<script setup lang="ts">
import { computed } from "vue";
import { useTranslation } from "i18next-vue";
import CardDashedButton from "./CardDashedButton.vue";

/**
 * Ligne « ajouter des éléments » d'une carte (groupe IA, observation), dans
 * la continuité de sa liste d'entités. Trois états :
 * - mode ajout actif : « Cliquez sur les éléments à rajouter » (un tap en sort) ;
 * - des éléments hors de la carte sont sélectionnés sur le plan : « Ajouter
 *   les N éléments sélectionnés » ;
 * - sinon : « Ajouter des éléments », qui entre en mode ajout.
 */

const props = defineProps<{
  /** Mode « cliquez pour ajouter » actif sur cette carte. */
  addMode: boolean;
  /** Nb d'éléments sélectionnés sur le plan et absents de la carte. */
  extraSelectedCount: number;
}>();

const emit = defineEmits<{
  (e: "add-selected"): void;
  (e: "toggle-add-mode"): void;
}>();

const { t } = useTranslation();

const label = computed(() => {
  if (props.addMode) return t("canvas:analysis.entityList.clickToAdd");
  if (props.extraSelectedCount) {
    return t("canvas:analysis.entityList.addSelectedCount", {
      count: props.extraSelectedCount,
    });
  }
  return t("canvas:analysis.entityList.addElements");
});

const handleClick = (): void => {
  if (props.addMode) emit("toggle-add-mode");
  else if (props.extraSelectedCount) emit("add-selected");
  else emit("toggle-add-mode");
};
</script>

<template>
  <CardDashedButton
    class="entity-add-row"
    :icon="addMode ? 'crosshair' : 'plus-circle'"
    :label="label"
    :active="addMode"
    @click="handleClick"
  />
</template>

<style lang="scss" scoped>
// Dans la continuité de la liste d'entités, juste dessous.
.entity-add-row {
  margin-top: 2px;
}
</style>
